import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ButtonBase, Icon, Typography, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { withStyles } from '@mui/styles';

import { LoadingView } from './LoadingView';
import { StoreUtil, SpecialtyUtil } from 'doctivity-shared/utils';
import { ColorUtil, ServiceLineUtil } from 'utils';
import { useSelector } from 'react-redux';

const styles = (theme) => ({
  // Table container
  tableContainer: {
    width: '100%',
    fontSize: 12,
  },
  noData: {
    textAlign: 'center',
    paddingTop: 50,
    minHeight: 200,
  },
  // Legend
  colorKey: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 400,
    minHeight: 40,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  colorKeyIcon: {
    width: 14,
    height: 14,
    marginRight: 4,
    borderRadius: 2,
  },
  colorKeyText: {
    marginRight: 16,
  },
  // Header row
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#EBF1F8',
    borderRadius: '4px 4px 0 0',
    padding: '8px 0',
    marginBottom: 2,
  },
  headerCell: {
    color: '#64748B',
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    padding: '0 8px',
  },
  // Data rows
  dataRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 0',
    borderRadius: 2,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: '#F8FAFC',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#F8FAFC',
    },
  },
  cell: {
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Column widths for providers
  iconCol: {
    width: '2%',
    minWidth: 28,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      width: 24,
      minWidth: 24,
      padding: 0,
    },
  },
  nameCol: {
    width: '18%',
    minWidth: 120,
    [theme.breakpoints.down('md')]: {
      width: '40%',
      minWidth: 0,
      flexShrink: 0,
    },
  },
  specialtyCol: {
    width: '12%',
    minWidth: 90,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  orgCol: {
    width: '16%',
    minWidth: 100,
    [theme.breakpoints.down('md')]: {
      flex: 1,
      minWidth: 0,
    },
  },
  affiliatedPctCol: {
    width: 90,
    flexShrink: 0,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      width: 80,
      minWidth: 80,
    },
  },
  chartCol: {
    flex: 1,
    minWidth: 150,
    paddingRight: 12,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  // Column widths for service lines
  serviceLineNameCol: {
    width: '25%',
    minWidth: 150,
    [theme.breakpoints.down('md')]: {
      flex: 1,
      minWidth: 0,
    },
  },
  serviceLineChartCol: {
    flex: 1,
    minWidth: 200,
    paddingRight: 12,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  // Service line icon column (hidden on mobile since no affiliated icon)
  serviceLineIconCol: {
    width: 40,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  // Cell content styles
  providerName: {
    fontWeight: 500,
    color: '#212529',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  serviceLineName: {
    fontWeight: 500,
    color: '#212529',
    fontSize: 12,
  },
  specialty: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  orgName: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  affiliatedIcon: {
    fontSize: 14,
    color: '#5391D5',
  },
  affiliatedPct: {
    fontSize: 11,
    fontWeight: 500,
    color: '#212529',
  },
  // Stacked bar chart
  barContainer: {
    width: '100%',
    height: 18,
    backgroundColor: '#F3F4F6',
    borderRadius: 2,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
  },
  affiliatedBar: {
    height: '100%',
    backgroundColor: ColorUtil.AffiliatedColor,
    transition: 'width 0.5s ease-in-out',
  },
  unaffiliatedBar: {
    height: '100%',
    backgroundColor: ColorUtil.NonAffiliatedColor,
    transition: 'width 0.5s ease-in-out',
  },
  // Tooltip styles
  infoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
  networkTooltip: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    fontWeight: 400,
  },
  networkTooltipProviderLabel: {
    fontWeight: 500,
  },
  networkTooltipAffiliation: {
    fontSize: 10,
    fontWeight: 600,
    marginTop: 4,
    marginBottom: 4,
  },
  networkTooltipText: {
    fontWeight: 300,
  },
});

const WhiteTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: '#000',
    boxShadow: theme.shadows[1],
    fontSize: 14,
    fontWeight: 400,
    border: `1px solid ${theme.palette.tertiary.accent}`,
    borderRadius: 0,
    maxWidth: '25rem',
  },
}))(Tooltip);

function SharedAffiliationChart({
  groupByServicelines,
  classes,
  hasChildren,
  serviceline,
  before,
  isMarket,
  onServicelineClick,
  limit,
  legend,
  compact,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Use compact layout when compact prop is true OR on mobile
  const useCompactLayout = compact || isMobile;
  
  const networkData = useSelector((state) =>
    StoreUtil.get(state.analytics, 'CLAIMS_SHARED_AFFILIATION'),
  );
  const networkByServiceline = useSelector((state) =>
    StoreUtil.get(state.analytics, 'CLAIMS_SHARED_AFFILIATION_BY_SERVICELINE'),
  );
  const servicelines = useSelector(
    (state) => state.claims && state.claims.servicelines_grouped,
  );
  const network = groupByServicelines ? networkByServiceline : networkData;
  let data = network?.data?.rows ?? [];
  if (limit) {
    data = data.slice(0, limit);
  }
  const isLoaded = StoreUtil.isLoaded(network);
  let max = 15;
  data.forEach((row) => {
    max = Math.max(max, row.shared_patients);
  });

  const getAffiliatedIcon = useCallback(() => {
    return (
      <Tooltip
        placement='bottom-start'
        title={
          <Typography className={classes.infoTip}>
            Affiliated Provider
          </Typography>
        }
      >
        <Icon className={classes.affiliatedIcon}>hub</Icon>
      </Tooltip>
    );
  }, [classes]);

  // Build tooltip content
  const buildTooltip = (row, providerName, orgName, percent) => {
    const sl = ServiceLineUtil.findServiceLine(servicelines, serviceline);
    const serviceLineText = serviceline ? sl?.name ?? sl?.abbreviation : 'All Service Lines';
    
    if (groupByServicelines) {
      return (
        <div className={classes.networkTooltip}>
          <span className={classes.networkTooltipProviderLabel}>
            {providerName}
          </span>
          <span className={classes.networkTooltipText}>
            {percent.toFixed(2)}% of the patients in {providerName} seen&nbsp;
            {before ? 'before' : 'after'} the providers in this&nbsp;
            {isMarket ? 'Market' : 'Label'} are by Affiliated Providers.
          </span>
        </div>
      );
    } else {
      return (
        <div className={classes.networkTooltip}>
          <span className={classes.networkTooltipProviderLabel}>
            {providerName}
            {orgName && <span style={{ fontWeight: 300 }}> - {orgName}</span>}
          </span>
          <span className={classes.networkTooltipAffiliation}>
            {row.is_affiliated ? 'AFFILIATED' : 'NON-AFFILIATED'}
          </span>
          <span className={classes.networkTooltipText}>
            {percent.toFixed(2)}% of the patients seen&nbsp;
            {before ? 'before' : 'after'} {providerName} in&nbsp;
            {serviceLineText} are seen by Affiliated Providers.
          </span>
        </div>
      );
    }
  };

  if (!(network && isLoaded)) {
    return <LoadingView />;
  }

  if (isLoaded && data.length === 0) {
    return (
      <Typography className={classes.noData}>
        {!hasChildren
          ? 'No sub-servicelines available.'
          : 'No shared patients.'}
      </Typography>
    );
  }

  // Compact view - simplified layout for embedded contexts (e.g., TagDetailsPage)
  if (useCompactLayout) {
    return (
      <div className={classes.tableContainer}>
        {/* Header */}
        <div className={classes.headerRow}>
          {groupByServicelines ? (
            <>
              <div className={`${classes.headerCell} ${classes.serviceLineNameCol}`} style={{ flex: 1, minWidth: 0, width: 'auto' }}>Service Line</div>
              <div className={`${classes.headerCell} ${classes.affiliatedPctCol}`}>% Affiliated</div>
            </>
          ) : (
            <>
              <div className={`${classes.headerCell} ${classes.iconCol}`} style={{ width: 24, minWidth: 24 }}></div>
              <div className={`${classes.headerCell} ${classes.nameCol}`} style={{ flex: 1 }}>Provider</div>
              <div className={`${classes.headerCell} ${classes.affiliatedPctCol}`}>% Affiliated</div>
            </>
          )}
        </div>

        {/* Data Rows */}
        {isLoaded &&
          data?.length > 0 &&
          data?.map((row, index) => {
            let percent = 0;
            if (row.shared_patients && row.shared_patients_affiliated) {
              percent = (row.shared_patients_affiliated / row.shared_patients) * 100;
            }

            const providerName = groupByServicelines
              ? row.name
              : `${row.last_name}, ${row.first_name}`;
            const orgName = row.organization_name || '';
            
            const isBySL = groupByServicelines;
            const url = !isBySL ? `/providers/${row.id}/market` : undefined;
            const title = buildTooltip(row, providerName, orgName, percent);

            return (
              <WhiteTooltip arrow followCursor title={title} key={`provider:${row.id}`}>
                <ButtonBase
                  className={classes.dataRow}
                  href={url}
                  onClick={isBySL && onServicelineClick ? () => onServicelineClick(row) : undefined}
                >
                  {groupByServicelines ? (
                    <>
                      {/* Service Line Name */}
                      <div className={`${classes.cell} ${classes.serviceLineNameCol}`} style={{ flex: 1, minWidth: 0, width: 'auto' }}>
                        <span className={classes.serviceLineName}>{providerName}</span>
                      </div>

                      {/* % Affiliated */}
                      <div className={`${classes.cell} ${classes.affiliatedPctCol}`}>
                        <span className={classes.affiliatedPct}>{percent.toFixed(1)}%</span>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Icon Column */}
                      <div className={`${classes.cell} ${classes.iconCol}`} style={{ width: 24, minWidth: 24 }}>
                        {row.is_affiliated && getAffiliatedIcon()}
                      </div>

                      {/* Provider Name */}
                      <div className={`${classes.cell} ${classes.nameCol}`} style={{ flex: 1 }}>
                        <span className={classes.providerName}>
                          {providerName}
                        </span>
                      </div>

                      {/* % Affiliated */}
                      <div className={`${classes.cell} ${classes.affiliatedPctCol}`}>
                        <span className={classes.affiliatedPct}>{percent.toFixed(1)}%</span>
                      </div>
                    </>
                  )}
                </ButtonBase>
              </WhiteTooltip>
            );
          })}
      </div>
    );
  }

  // Full desktop view
  return (
    <div className={classes.tableContainer}>
      {/* Legend */}
      {isLoaded && data?.length > 0 && !!legend && (
        <div className={classes.colorKey}>
          <div
            className={classes.colorKeyIcon}
            style={{ backgroundColor: ColorUtil.AffiliatedColor }}
          />
          <div className={classes.colorKeyText}>Affiliated</div>
          <div
            className={classes.colorKeyIcon}
            style={{ backgroundColor: ColorUtil.NonAffiliatedColor }}
          />
          <div className={classes.colorKeyText}>Non-Affiliated</div>
        </div>
      )}

      {/* Header */}
      <div className={classes.headerRow}>
        {groupByServicelines ? (
          <>
            <div className={`${classes.headerCell} ${classes.serviceLineIconCol}`}></div>
            <div className={`${classes.headerCell} ${classes.serviceLineNameCol}`}>Service Line</div>
            <div className={`${classes.headerCell} ${classes.affiliatedPctCol}`}>% Affiliated</div>
            <div className={`${classes.headerCell} ${classes.serviceLineChartCol}`}></div>
          </>
        ) : (
          <>
            <div className={`${classes.headerCell} ${classes.iconCol}`}></div>
            <div className={`${classes.headerCell} ${classes.nameCol}`}>Provider</div>
            <div className={`${classes.headerCell} ${classes.specialtyCol}`}>Specialty</div>
            <div className={`${classes.headerCell} ${classes.orgCol}`}>Organization</div>
            <div className={`${classes.headerCell} ${classes.affiliatedPctCol}`}>% Affiliated</div>
            <div className={`${classes.headerCell} ${classes.chartCol}`}></div>
          </>
        )}
      </div>

      {/* Data Rows */}
      {isLoaded &&
        data?.length > 0 &&
        data?.map((row, index) => {
          let percent = 0;
          if (row.shared_patients && row.shared_patients_affiliated) {
            percent = (row.shared_patients_affiliated / row.shared_patients) * 100;
          }

          const providerName = groupByServicelines
            ? row.name
            : `${row.last_name}, ${row.first_name}`;
          const orgName = row.organization_name || '';
          const specialty = row.taxonomy ? SpecialtyUtil.getNameFromTaxonomy(row.taxonomy) : '';
          
          const isBySL = groupByServicelines;
          const url = !isBySL ? `/providers/${row.id}/market` : undefined;
          const title = buildTooltip(row, providerName, orgName, percent);
          
          // Calculate bar width based on shared_patients relative to max
          const barWidth = row.shared_patients ? (row.shared_patients / max) * 100 : 0;

          return (
            <WhiteTooltip arrow followCursor title={title} key={`provider:${row.id}`}>
              <ButtonBase
                className={classes.dataRow}
                href={url}
                onClick={isBySL && onServicelineClick ? () => onServicelineClick(row) : undefined}
              >
                {groupByServicelines ? (
                  <>
                    {/* Icon Column - hidden on mobile for service lines */}
                    <div className={`${classes.cell} ${classes.serviceLineIconCol}`}></div>

                    {/* Service Line Name */}
                    <div className={`${classes.cell} ${classes.serviceLineNameCol}`}>
                      <span className={classes.serviceLineName}>{providerName}</span>
                    </div>

                    {/* % Affiliated */}
                    <div className={`${classes.cell} ${classes.affiliatedPctCol}`}>
                      <span className={classes.affiliatedPct}>{percent.toFixed(1)}%</span>
                    </div>

                    {/* Stacked Bar Chart */}
                    <div className={`${classes.cell} ${classes.serviceLineChartCol}`}>
                      <div className={classes.barContainer} style={{ width: `${barWidth}%` }}>
                        <div
                          className={classes.affiliatedBar}
                          style={{ width: `${percent}%` }}
                        />
                        <div
                          className={classes.unaffiliatedBar}
                          style={{ width: `${100 - percent}%` }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Icon Column */}
                    <div className={`${classes.cell} ${classes.iconCol}`}>
                      {row.is_affiliated && getAffiliatedIcon()}
                    </div>

                    {/* Provider Name */}
                    <div className={`${classes.cell} ${classes.nameCol}`}>
                      <span className={classes.providerName}>
                        {providerName}
                      </span>
                    </div>

                    {/* Specialty */}
                    <div className={`${classes.cell} ${classes.specialtyCol}`}>
                      <span className={classes.specialty}>
                        {specialty || '-'}
                      </span>
                    </div>

                    {/* Organization */}
                    <div className={`${classes.cell} ${classes.orgCol}`}>
                      <span className={classes.orgName}>
                        {orgName || '-'}
                      </span>
                    </div>

                    {/* % Affiliated */}
                    <div className={`${classes.cell} ${classes.affiliatedPctCol}`}>
                      <span className={classes.affiliatedPct}>{percent.toFixed(1)}%</span>
                    </div>

                    {/* Stacked Bar Chart */}
                    <div className={`${classes.cell} ${classes.chartCol}`}>
                      <div className={classes.barContainer} style={{ width: `${barWidth}%` }}>
                        <div
                          className={classes.affiliatedBar}
                          style={{ width: `${percent}%` }}
                        />
                        <div
                          className={classes.unaffiliatedBar}
                          style={{ width: `${100 - percent}%` }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </ButtonBase>
            </WhiteTooltip>
          );
        })}
    </div>
  );
}

SharedAffiliationChart.propTypes = {
  groupByServicelines: PropTypes.bool,
  classes: PropTypes.object,
  hasChildren: PropTypes.bool,
  serviceline: PropTypes.string,
  before: PropTypes.bool,
  isMarket: PropTypes.bool,
  onServicelineClick: PropTypes.func,
  limit: PropTypes.number,
  legend: PropTypes.bool,
  compact: PropTypes.bool,
};

const StyledSharedAffiliationChart = withStyles(styles)(SharedAffiliationChart);
export { StyledSharedAffiliationChart as SharedAffiliationChart };